import { toast } from "react-toastify";

import {
  UpdateAgenticLogicRuleVersionRequestBodyType,
  UpdateAgenticLogicRuleVersionRequestPathParamsType,
  UpdateAgenticLogicRuleVersionResponseBodyType,
} from "@intelligems/ig-types";
import { getMutationErrorMessage } from "@src/queryClient";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { agenticLogicKeys } from "../queryKeys";
import { ApiRequest } from "../types/api";

const updateAgenticLogicRuleVersion = async (
  payload: ApiRequest<
    UpdateAgenticLogicRuleVersionRequestBodyType,
    UpdateAgenticLogicRuleVersionRequestPathParamsType
  >
) => {
  const resp = await api.patch<
    UpdateAgenticLogicRuleVersionResponseBodyType,
    UpdateAgenticLogicRuleVersionRequestBodyType
  >(
    `/v2/agentic-logic/rules/${payload.pathParams.ruleId}/versions/${payload.pathParams.version}`,
    payload.body
  );
  return resp.data;
};

/**
 * Renames one published version. The only mutable field a frozen version has:
 * everything else about it is the artifact set that shipped, and rewriting any
 * of that would make the version stop describing what served.
 *
 * `title: null` clears the rename, and the card falls back to its default
 * `v{n}` label — which is why the title is nullable rather than an empty
 * string: "" is a title the merchant typed, `null` is the absence of one.
 *
 * Invalidates the same rows a delete does. The title rides both the version
 * LIST DTO (the cards) and the version DETAIL DTO (an expanded card's header),
 * so a rename that refreshed only the list would leave the open card on the
 * old name.
 */
export const useUpdateAgenticLogicRuleVersion = () => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateAgenticLogicRuleVersionResponseBodyType,
    Error,
    ApiRequest<
      UpdateAgenticLogicRuleVersionRequestBodyType,
      UpdateAgenticLogicRuleVersionRequestPathParamsType
    >,
    unknown
  >({
    mutationFn: async (data) => updateAgenticLogicRuleVersion(data),
    onSuccess: async (_data, payload) => {
      const { ruleId, version } = payload.pathParams;
      await queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.versions(ruleId),
      });
      await queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.versionDetail(ruleId, Number(version)),
      });
      await queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.rule(ruleId),
      });
    },
    onError: (error) => {
      const errorDetail = getMutationErrorMessage(error);
      toast.error(
        `Failed to rename version${errorDetail ? `. ${errorDetail}` : ""}`
      );
    },
  });
};
